import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { IDEA_CATEGORIES } from "@/lib/constants";

export default function AddIdeaDialog({ open, onOpenChange, onSaved, idea }) {
  const editing = Boolean(idea);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Blog ideas");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(idea?.title || "");
      setCategory(idea?.category || "Blog ideas");
      setNotes(idea?.notes || "");
    }
  }, [open, idea]);

  const save = async () => {
    if (!title.trim()) { toast.error("Please give your idea a title."); return; }
    setSaving(true);
    try {
      const body = { title: title.trim(), category, notes, status: idea?.status || "New", tags: [] };
      if (editing) await api.put(`/ideas/${idea.id}`, body);
      else await api.post("/ideas", body);
      toast.success(editing ? "Idea updated." : "Idea saved.");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="idea-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{editing ? "Edit idea" : "Save an idea"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input data-testid="idea-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="A gentle spark to hold onto" className="rounded-xl" autoFocus />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-xl" data-testid="idea-category-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                {IDEA_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Short description</Label>
            <Textarea data-testid="idea-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="A sentence or two to capture the idea…" className="rounded-xl min-h-[110px]" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="idea-save-button" className="rounded-full bg-primary hover:bg-primary/90">
            {editing ? "Save changes" : "Save idea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
